import { useState, useEffect, useMemo } from 'react';
import { staffAPI } from '../services/api';

export default function useDRCData() {
  const [drcData, setDrcData] = useState(null);
  const [drcLoading, setDrcLoading] = useState(true);
  const [drcViewMode, setDrcViewMode] = useState('ttm');

  useEffect(() => {
    const fetchDRCData = async () => {
      try {
        setDrcLoading(true);
        const response = await staffAPI.getDRCDashboardStatus();
        const data = response.data;
        setDrcData(data);

        const hasEmployeeData = data?.workforce_breakdown?.local_employees > 0 || data?.workforce_breakdown?.foreign_workers > 0;
        if (!hasEmployeeData) {
          console.info('ℹ️ No employee data found in Foreign Worker Quota calculations.');
        }
      } catch (error) {
        console.error('❌ DRC data failed to load:', error?.message);
        setDrcData({
          overall_status: 'error',
          status_message: 'Unable to load compliance data',
          status_color: 'gray',
          drc_metrics: {
            current_percentage: 0, company_target: 25.0, regulatory_limit: 83.3,
            company_compliant: false, regulatory_compliant: false, company_buffer: 0, regulatory_buffer: 0,
          },
          s_pass_metrics: {
            current_percentage: 0, limit: 15.0, current_count: 0,
            limit_count: 0, compliant: false, available_slots: 0, buffer: 0,
          },
          lqs_metrics: {
            effective_local_count: 0, total_local_employees: 0,
            full_weight_employees: 0, half_weight_employees: 0, lqs_efficiency: 0,
          },
          workforce_breakdown: {
            total_workforce: 0, local_employees: 0, foreign_workers: 0,
            work_permit_holders: 0, s_pass_holders: 0, ep_holders: 0,
          },
          quota_limits: {
            work_permit_quota: 0, available_wp_slots: 0, s_pass_limit: 0, available_s_pass_slots: 0,
          },
          strategic_insights: {
            can_hire_foreign_workers: false, recommended_action: 'Please check authentication and try again',
            growth_capacity: 0, risk_level: 'unknown',
          },
          last_updated: new Date().toISOString(),
          data_freshness: 'error',
        });
      } finally {
        setDrcLoading(false);
      }
    };
    fetchDRCData();
  }, []);

  // Derived values
  const effectiveLqsAvg = Number(drcData?.lqs_metrics?.effective_local_count_3m_avg ?? 0);
  const effectiveLqs = Number(drcData?.lqs_metrics?.effective_local_count ?? 0);
  const localEmployeesCount = Number(drcData?.workforce_breakdown?.local_employees ?? 0);
  const wpCount = Number(drcData?.workforce_breakdown?.work_permit_holders ?? 0);
  const spCount = Number(drcData?.workforce_breakdown?.s_pass_holders ?? 0);
  const epCount = Number(drcData?.workforce_breakdown?.ep_holders ?? 0);
  const foreignTotal = Number(drcData?.workforce_breakdown?.foreign_workers ?? (wpCount + spCount + epCount));

  const lqsCountForDrc = Number(
    drcData?.drc_metrics?.lqs_for_drc ?? (effectiveLqsAvg > 0 ? effectiveLqsAvg : (effectiveLqs > 0 ? effectiveLqs : localEmployeesCount))
  );
  const migrantCountCanonical = Number(drcData?.drc_metrics?.migrant_workers ?? (wpCount + spCount));
  const drcRatio = Number(
    drcData?.drc_metrics?.current_ratio ?? (lqsCountForDrc > 0 ? migrantCountCanonical / lqsCountForDrc : 0)
  );
  const drcRatioDisplay = drcRatio.toFixed(2);

  const lqsCount = lqsCountForDrc;
  const migrantCount = migrantCountCanonical;
  const foreignShare = (lqsCount + migrantCount) > 0 ? (migrantCount / (lqsCount + migrantCount)) * 100 : 0;
  const foreignShareDisplay = foreignShare.toFixed(1);

  const momLimit = Number(drcData?.drc_metrics?.regulatory_limit_ratio ?? 5.0);
  const maxForeignWorkers = Math.floor(lqsCount * momLimit);
  const bufferCapacity = Math.max(0, maxForeignWorkers - migrantCount);

  // Chart data
  const ttmChartData = useMemo(() => {
    const formatMonth = (d) => {
      try {
        const date = new Date(d);
        if (!isNaN(date)) {
          const short = date.toLocaleString('en-SG', { month: 'short' });
          const yy = String(date.getFullYear()).slice(-2);
          return `${short} '${yy}`;
        }
      } catch (_) {}
      return d;
    };

    const pickMonthly = () => {
      const candidates = [
        drcData?.historical?.monthly, drcData?.drc_history?.monthly,
        drcData?.history?.monthly, drcData?.monthly_snapshots,
        drcData?.snapshots, drcData?.trend?.ttm, drcData?.trends?.ttm,
      ];
      return candidates.find(arr => Array.isArray(arr) && arr.length > 0) || null;
    };

    const monthly = pickMonthly();
    if (monthly) {
      const now = new Date();
      const normalized = monthly.map((row) => {
        const foreign = Number(row?.foreign_workers ?? row?.foreign ?? row?.fw ?? row?.foreign_count ?? 0);
        const local = Number(row?.local_employees ?? row?.locals ?? row?.le ?? row?.local_count ?? 0);
        const direct = Number(row?.drc ?? row?.drc_ratio ?? row?.ratio ?? 0);
        const ratio = direct || (local > 0 ? foreign / local : 0);

        let monthLabel;
        if (row?.month) {
          monthLabel = row.month;
        } else {
          const label = row?.label || row?.period || row?.date || row?.calculation_date;
          monthLabel = formatMonth(label);
        }

        const dateStr = row?.calculation_date || row?.date || row?.label || row?.period;
        const date = new Date(dateStr);
        const isCurrentMonth = !isNaN(date) && date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth();
        const isFutureMonth = !isNaN(date) && date > now;
        if (isCurrentMonth || isFutureMonth) return null;

        return { month: monthLabel, drc: Number(ratio.toFixed(2)) };
      })
      .filter(Boolean)
      .slice(-12);
      if (normalized.length > 0) return normalized;
    }

    const now = new Date();
    const ttmMonths = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - 1 - i, 1);
      const short = d.toLocaleString('en-SG', { month: 'short' });
      const yy = String(d.getFullYear()).slice(-2);
      ttmMonths.push(`${short} '${yy}`);
    }
    return ttmMonths.map((m) => ({ month: m, drc: null }));
  }, [drcData, drcRatioDisplay]);

  const yoyYears = useMemo(() => {
    const y = new Date().getFullYear();
    return [y, y - 1, y - 2];
  }, []);

  const yoyChartData = useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const yCurKey = `y${currentYear}`;
    const yPrevKey = `y${currentYear - 1}`;
    const yPrev2Key = `y${currentYear - 2}`;

    const monthly = drcData?.historical?.monthly || drcData?.drc_history?.monthly || drcData?.history?.monthly;
    if (Array.isArray(monthly) && monthly.length >= 24) {
      const points = monthly.map(r => {
        const foreign = Number(r?.foreign_workers ?? 0);
        const local = Number(r?.local_employees ?? 0);
        const direct = Number(r?.drc ?? r?.drc_ratio ?? 0);
        const dateKey = r?.date || r?.period || r?.month || r?.calculation_date;
        const d = new Date(dateKey);
        const ratio = direct || (local > 0 ? foreign / local : 0);
        return { y: d.getFullYear(), m: d.getMonth(), r: Number(ratio.toFixed(2)) };
      }).filter(p => !isNaN(p.y) && p.m >= 0 && p.m <= 11);

      const targetMonths = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      const byYM = {};
      for (const p of points) byYM[`${p.y}-${p.m}`] = p.r;

      return targetMonths.map((name, idx) => {
        const result = {
          month: name,
          [yCurKey]: byYM[`${currentYear}-${idx}`],
          [yPrevKey]: byYM[`${currentYear - 1}-${idx}`],
          [yPrev2Key]: byYM[`${currentYear - 2}-${idx}`],
        };
        if (idx >= currentMonth) {
          result[yCurKey] = null;
        }
        return result;
      });
    }

    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const fallbackPrev2 = [2.2,2.1,2.0,2.05,2.15,2.2,2.25,2.3,2.1,2.05,2.0,1.95];
    const fallbackPrev = [3.0,2.9,2.8,2.6,2.4,2.3,2.25,2.3,2.8,2.85,2.9,3.0];
    const fallbackCurFull = [3.2,3.0,2.7,2.6,2.4,2.3,2.5,2.6,Number(drcRatioDisplay),2.55,2.6,2.65];
    const fallbackCur = fallbackCurFull.map((v, idx) => (idx >= currentMonth ? null : v));
    return months.map((m, i) => ({ month: m, [yPrev2Key]: fallbackPrev2[i], [yPrevKey]: fallbackPrev[i], [yCurKey]: fallbackCur[i] }));
  }, [drcData, drcRatioDisplay]);

  const yoyYAxisDomain = useMemo(() => {
    if (!yoyChartData || yoyChartData.length === 0) return [1, 4];
    const cy = new Date().getFullYear();
    const yearKeys = [`y${cy}`, `y${cy - 1}`, `y${cy - 2}`];
    const allValues = [];
    yoyChartData.forEach(point => {
      yearKeys.forEach(k => {
        if (point[k] != null && !isNaN(point[k])) allValues.push(point[k]);
      });
    });
    if (allValues.length === 0) return [1, 4];
    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);
    const padding = (maxValue - minValue) * 0.1;
    return [Math.floor(Math.max(0, minValue - padding) * 2) / 2, Math.ceil((maxValue + padding) * 2) / 2];
  }, [yoyChartData]);

  const ttmYAxisDomain = useMemo(() => {
    if (!ttmChartData || ttmChartData.length === 0) return [0, 5];
    const allValues = ttmChartData.map(d => d.drc).filter(v => v != null && !isNaN(v));
    if (allValues.length === 0) return [0, 5];
    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);
    const padding = (maxValue - minValue) * 0.1;
    return [Math.floor(Math.max(0, minValue - padding) * 2) / 2, Math.ceil((maxValue + padding) * 2) / 2];
  }, [ttmChartData]);

  const ttmValues = ttmChartData.map(d => d.drc).filter(v => v !== null && !isNaN(v));
  const minDrc = ttmValues.length > 0 ? Math.min(...ttmValues) : 0;
  const maxDrc = ttmValues.length > 0 ? Math.max(...ttmValues) : 0;
  const trendRange = `${minDrc.toFixed(1)}-${maxDrc.toFixed(1)}`;

  return {
    drcData, drcLoading, drcViewMode, setDrcViewMode,
    drcRatio, drcRatioDisplay,
    lqsCount, migrantCount, foreignTotal, foreignShare, foreignShareDisplay,
    wpCount, spCount, epCount, localEmployeesCount,
    momLimit, maxForeignWorkers, bufferCapacity,
    ttmChartData, yoyChartData, ttmYAxisDomain, yoyYAxisDomain,
    trendRange, minDrc, maxDrc,
    yoyYears,
  };
}
